/**
 * Composes plan 0's `MeterBar` over a tenant's capacity (platform design §5.1.1). Pure and
 * presentational — no query of its own — so a consuming screen fetches with `useCapacity` and
 * passes the result straight through.
 *
 * `campaign_slots === null` (no capacity row at all) and `campaign_slots === 0` (a measured zero)
 * both render the track alone: `§4.5`'s absence rule for the first, and for the second there is
 * no positive `max` a fraction could be taken against — CA-6's own ruling for its sidebar meter
 * ("renders nothing, not a full bar") applies here directly, since it reuses this component.
 * `slots_available < 0` (a reduction below the number in use, §5.1.1: legal, never destructive)
 * fills the bar at capacity rather than clamping past it invisibly. None of the three renders a
 * percentage — `slotUtilisation` returns `null` for all three, and only a non-null utilisation
 * gets a caption.
 */

import type { ReactNode } from 'react';

import { MeterBar } from '../../../chrome/MeterBar';
import { getLocale } from '../../../i18n';
import { percent } from '../../../i18n/format';
import type { Capacity } from './api/schemas';
import { cm } from './messages';

export type SlotMeterState = 'absent' | 'over' | 'empty' | 'partial' | 'full';

export function slotMeterState(capacity: Capacity): SlotMeterState {
  const { campaign_slots, campaigns_in_use, slots_available } = capacity;
  if (campaign_slots === null) return 'absent';
  if (slots_available !== null && slots_available < 0) return 'over';
  if (campaign_slots === 0 || campaigns_in_use === 0) return 'empty';
  if (campaigns_in_use === campaign_slots) return 'full';
  return 'partial';
}

/** `null` for every state that renders no bar fraction or has none to show a percentage of:
 *  `absent`, `over` and `empty` (`campaign_slots <= 0` divides by nothing meaningful; a `campaigns_
 *  in_use === 0` empty organisation is `0%`, which the caption below still shows — that state is
 *  excluded from `null` on purpose, see the branch below). */
export function slotUtilisation(capacity: Capacity): number | null {
  const state = slotMeterState(capacity);
  if (state === 'absent' || state === 'over') return null;
  if (capacity.campaign_slots === null || capacity.campaign_slots === 0) return null;
  return capacity.campaigns_in_use / capacity.campaign_slots;
}

function slotMeterBar(capacity: Capacity, state: SlotMeterState, label: string): ReactNode {
  const { campaign_slots: campaignSlots, campaigns_in_use: campaignsInUse } = capacity;
  // No positive max means no fraction to draw — `absent`, a real `campaign_slots === 0`, and the
  // degenerate `over`-with-zero-slots case all fall through to the plain track.
  if (campaignSlots === null || campaignSlots === 0) {
    return <MeterBar value={null} max={1} label={label} />;
  }
  if (state === 'over') {
    return <MeterBar value={campaignSlots} max={campaignSlots} label={label} />;
  }
  return <MeterBar value={campaignsInUse} max={campaignSlots} label={label} />;
}

export function SlotMeter(props: {
  capacity: Capacity;
  /** `CapacityStats`' fraction panel (Figma `185:5432`) already states the fraction as its own
   *  value line and draws no percentage beside the bar. */
  hidePercentage?: boolean;
}): ReactNode {
  const { capacity, hidePercentage = false } = props;
  const state = slotMeterState(capacity);
  const label = cm('meter.label');
  const locale = getLocale();
  const utilisation = slotUtilisation(capacity);
  const bar = slotMeterBar(capacity, state, label);

  return (
    <div data-testid="slot-meter" data-state={state}>
      {bar}
      {!hidePercentage && utilisation !== null && (
        <p data-testid="slot-meter-percentage" className="text-caption text-muted-foreground">
          {percent(utilisation, locale)}
        </p>
      )}
    </div>
  );
}
